import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  InputAdornment,
  TextField,
  Typography,
  TablePagination,
  Skeleton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Search, Clear } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: '100%',
  maxWidth: '2000px',
  margin: 'auto',
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
}));

const BoldTableCell = styled(TableCell)(() => ({
  fontWeight: 'bold',
}));

const HoverTableRow = styled(TableRow)(() => ({
  '&:hover': {
    backgroundColor: '#e0f7fa',
  },
}));

const NoData = () => <span style={{ color: '#ccc' }}>No data</span>;

const defaultCell = (row, column) => {
  const value = row[column.key];
  return value ? <span>{value}</span> : <NoData />;
};

/**
 * Generic, config-driven list table that captures the shared CIRA dashboard UX:
 * search, pagination, skeleton loading, empty state, and per-row edit/delete actions.
 *
 * Props:
 *  - title:       Helmet <title>
 *  - columns:     [{ key, label, width?, render?(row) }]
 *  - rows:        full row array (filtering + pagination handled here)
 *  - loading:     show skeleton rows
 *  - searchKeys:  array of row keys the search box filters on
 *  - rowKey:      unique row field (defaults to 'id')
 *  - onEdit(row), onDelete(row): optional; when provided, an Actions column is rendered
 *  - deleting:    map of rowKey -> bool to show a per-row delete spinner
 *  - emptyMessage
 *  - toolbar:     optional ReactNode rendered next to the search box (e.g. a status filter)
 *  - filterFn:    optional (row)=>boolean applied in addition to the search box
 */
export default function DataTable({
  title,
  columns,
  rows = [],
  loading = false,
  searchKeys = [],
  rowKey = 'id',
  onEdit,
  onDelete,
  deleting = {},
  emptyMessage = 'No results found.',
  toolbar = null,
  filterFn = null,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasActions = Boolean(onEdit || onDelete);
  const colSpan = columns.length + (hasActions ? 1 : 0);

  // searchKeys items may be a row field name (string) or a (row)=>string selector.
  const fieldValue = (row, key) => (typeof key === 'function' ? key(row) : row[key]);

  const matchesSearch = (row) =>
    !searchTerm || !searchKeys.length ||
    searchKeys.some((key) => String(fieldValue(row, key) ?? '').toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredRows = rows.filter((row) => matchesSearch(row) && (filterFn ? filterFn(row) : true));

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <StyledTableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      {title && (
        <Helmet>
          <title>{title}</title>
        </Helmet>
      )}
      <Box sx={{ mb: 2, mt: 2, mr: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          variant="outlined"
          label="Search"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          size="small"
          sx={{ backgroundColor: 'white' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm ? (
                  <IconButton onClick={() => setSearchTerm('')}>
                    <Clear />
                  </IconButton>
                ) : (
                  <Search />
                )}
              </InputAdornment>
            ),
          }}
        />
        {toolbar}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <BoldTableCell key={column.key} sx={{ width: column.width }}>
                {column.label}
              </BoldTableCell>
            ))}
            {hasActions && <BoldTableCell sx={{ width: 100 }}>Actions</BoldTableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from(new Array(10)).map((_, index) => (
              <HoverTableRow key={index}>
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ width: column.width }}>
                    <Skeleton variant="text" height={40} width="100%" sx={{ bgcolor: 'grey.300' }} />
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell sx={{ width: 100 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Skeleton variant="rectangular" height={40} width={40} sx={{ bgcolor: 'grey.300' }} />
                      <Skeleton variant="rectangular" height={40} width={40} sx={{ bgcolor: 'grey.300' }} />
                    </Box>
                  </TableCell>
                )}
              </HoverTableRow>
            ))
          ) : filteredRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center">
                <Typography variant="h6" style={{ color: 'red' }}>{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row) => (
              <HoverTableRow key={row[rowKey]}>
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ width: column.width }}>
                    {column.render ? column.render(row) : defaultCell(row, column)}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {onEdit && (
                        <Tooltip title="Edit" placement="bottom">
                          <IconButton onClick={() => onEdit(row)} sx={{ marginRight: '8px' }}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDelete && (
                        <Tooltip title="Delete" placement="bottom">
                          <IconButton onClick={() => onDelete(row)} disabled={deleting[row[rowKey]]}>
                            {deleting[row[rowKey]] ? <CircularProgress size={24} /> : <Delete sx={{ color: 'red' }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </HoverTableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!loading && filteredRows.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
          sx={{ mt: 2 }}
        />
      )}
    </StyledTableContainer>
  );
}
